import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

/**
 * "What partners tell us they need" — reframed from placeholder testimonials.
 * These are WHO's observations from early pilot conversations, NOT attributed
 * quotes from signed partners. Named case studies will publish once pilots
 * complete their first quarter and organizations approve on-record quotes.
 */
const OBSERVATIONS = [
  {
    tag: "Veteran Service Organizations",
    heading: "Someone real, on the first call.",
    body: "Frontline teams tell us the single biggest gap is the moment a veteran reaches a phone tree instead of a person. WHO Guides™ pick up on that first call — and stay on until a real connection is made.",
  },
  {
    tag: "Healthcare & Community Providers",
    heading: "A warm handoff, not a cold email.",
    body: "Caseworkers do not need another database. They need context, consent, and clear ownership of the next step. WHO gives them a completed handoff to start from — so their work begins with a relationship, not a lookup.",
  },
  {
    tag: "Funders & Public Agencies",
    heading: "Visibility after the referral.",
    body: "Funders consistently ask the same question: what actually happened after the referral was made? WHO's closed-loop pathway is designed so agencies can answer that with confidence — and adjust what they fund next.",
  },
];

export default function Testimonials({
  eyebrow = "Partner Perspective",
  title = "What partners tell us they need.",
}) {
  return (
    <section className="py-24 lg:py-32 border-t border-borderSubtle bg-white" data-testid="testimonials-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <p className="eyebrow text-gold-deep">{eyebrow}</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
            {title}
          </h2>
          <p className="mt-4 text-slateSoft leading-relaxed max-w-2xl">
            The observations below reflect what early pilot conversations have surfaced most
            consistently. Named partner quotes and case studies will be published here once
            Pittsburgh pilot organizations complete their first quarter and approve on-record
            statements.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {OBSERVATIONS.map((t, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="relative p-8 bg-cream-panel border border-borderSubtle rounded-lg hover:border-gold transition-colors"
              data-testid={`testimonial-card-${i}`}
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold/15 text-gold-deep">
                  <Sparkles className="w-4 h-4" />
                </span>
                <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">
                  {t.tag}
                </p>
              </div>
              <h3 className="mt-5 font-display font-bold text-navy text-lg leading-tight">
                {t.heading}
              </h3>
              <p className="mt-3 text-navy/85 leading-relaxed">
                {t.body}
              </p>
              <p className="mt-6 pt-4 border-t border-borderSubtle text-[10px] uppercase tracking-widest text-slateSoft/70 font-bold">
                WHO Observation · Not an attributed quote
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
